"use client";

import { useState, type ReactNode } from "react";
import { parseStructuredPolicy, type BlockVariant, type TermsData } from "../terms";
import { LEGAL_DOCUMENTS, policyDocumentStyle, type PolicyDocumentStyle } from "../policyDocumentStyle";

/** Each app passes its own Markdown renderer (e.g. PolicyMarkdown from @dwelldrop/ui). */
export type RenderMarkdown = (markdown: string, variant?: BlockVariant) => ReactNode;

function isPrivacyPolicy(policyName: string): boolean {
  return policyName.startsWith("privacy_policy");
}

const BLOCK_STYLES: Record<BlockVariant, string> = {
  default: "",
  blue: "rounded border border-blue-200 bg-blue-50 p-3",
  green: "rounded border border-green-200 bg-green-50 p-3",
  yellow: "rounded border border-yellow-200 bg-yellow-50 p-3",
  purple: "rounded border border-purple-200 bg-purple-50 p-3",
  red: "rounded border border-red-200 bg-red-50 p-3",
};

function Block({
  text,
  variant,
  renderMarkdown,
}: {
  text: string;
  variant?: BlockVariant;
  renderMarkdown: RenderMarkdown;
}) {
  const v = variant ?? "default";
  return <div className={BLOCK_STYLES[v] ?? ""}>{renderMarkdown(text, v)}</div>;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function WhatsNew({
  lines,
  version,
  tone,
  padding,
}: {
  lines: string[];
  version: number | string;
  tone: "blue" | "green";
  padding: "p-3" | "p-4";
}) {
  return (
    <div
      className={`rounded-lg border bg-white ${padding} ${
        tone === "blue" ? "border-blue-300" : "border-green-300"
      }`}
    >
      <p
        className={`mb-2 text-center text-xs font-semibold ${
          tone === "blue" ? "text-blue-900" : "text-green-900"
        }`}
      >
        {"🎯"} What&apos;s New in Version {version}
      </p>
      <ul className="space-y-1 text-center text-xs text-gray-700">
        {lines.map((line, idx) => (
          <li key={idx}>&bull; {line}</li>
        ))}
      </ul>
    </div>
  );
}

function CloseButton({ onClose, light }: { onClose: () => void; light: boolean }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className={`shrink-0 p-1 transition-colors ${
        light ? "text-white/80 hover:text-white" : "text-gray-400 hover:text-gray-600"
      }`}
    >
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

export type PolicyDocumentHeaderProps = {
  readonly title: string;
  readonly policyName: string;
  readonly version: number | string;
  readonly effectiveDate?: string;
  readonly onClose?: () => void;
};

/** Document header as users see it: gradient banner for Terms/Privacy, plain header otherwise. */
export function PolicyDocumentHeader({
  title,
  policyName,
  version,
  effectiveDate,
  onClose,
}: PolicyDocumentHeaderProps) {
  const effective = effectiveDate?.trim();

  if (!LEGAL_DOCUMENTS.has(policyName)) {
    return (
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white p-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <p className="mt-0.5 text-[10px] text-gray-400">Version {version}</p>
        </div>
        {onClose && <CloseButton onClose={onClose} light={false} />}
      </div>
    );
  }

  const isPrivacy = isPrivacyPolicy(policyName);
  return (
    <div
      className={`sticky top-0 z-10 bg-gradient-to-r p-6 text-white ${
        isPrivacy ? "from-green-500 to-green-600" : "from-blue-500 to-blue-600"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              {isPrivacy ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              )}
            </svg>
          </div>
          <div>
            <h2 className="text-left text-2xl font-bold">{title}</h2>
            <p className={`text-left text-sm ${isPrivacy ? "text-green-100" : "text-blue-100"}`}>
              Version {version}
              {effective && ` - Effective ${effective}`}
            </p>
          </div>
        </div>
        {onClose && <CloseButton onClose={onClose} light />}
      </div>
    </div>
  );
}

export type PolicyDocumentProps = {
  readonly data: TermsData;
  readonly policyName: string;
  readonly version: number | string;
  readonly whatsNew?: string | null;
  readonly renderMarkdown: RenderMarkdown;
  /** Overrides the default style for this policy (e.g. a standalone page). */
  readonly style?: Partial<PolicyDocumentStyle>;
};

/** Body of a structured policy: intro, sections, footer meta and What's New. */
export function PolicyDocument({
  data,
  policyName,
  version,
  whatsNew,
  renderMarkdown,
  style,
}: PolicyDocumentProps) {
  const { layout, expandControls, footer, footerGap, footerNote } = {
    ...policyDocumentStyle(policyName),
    ...style,
  };
  const isPrivacy = isPrivacyPolicy(policyName);
  const isLegal = LEGAL_DOCUMENTS.has(policyName);
  const [expanded, setExpanded] = useState<Set<number>>(new Set([0]));
  const allOpen = data.sections.length > 0 && expanded.size === data.sections.length;

  const toggle = (i: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const expandAll = () => setExpanded(new Set(data.sections.map((_, i) => i)));
  const collapseAll = () => setExpanded(new Set());

  const whatsNewLines = (whatsNew ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const whatsNewTone = isLegal && !isPrivacy ? "blue" : "green";
  const linkClass = isPrivacy
    ? "text-green-600 hover:text-green-800"
    : "text-blue-600 hover:text-blue-800";

  const footerBoxClass =
    footer === "tinted"
      ? isPrivacy
        ? "border-green-200 bg-green-50"
        : "border-blue-200 bg-blue-50"
      : isPrivacy
        ? "border-green-200 bg-gradient-to-r from-green-50 to-blue-50"
        : "border-gray-200 bg-gradient-to-r from-gray-100 to-gray-100";

  return (
    <div className="space-y-4">
      {data.intro?.trim() && (
        <div
          className={`rounded-lg border p-4 text-left text-sm leading-relaxed text-gray-700 ${
            isPrivacy ? "border-green-200 bg-green-50" : "border-blue-200 bg-blue-50"
          }`}
        >
          {renderMarkdown(data.intro)}
        </div>
      )}

      {layout === "accordion" && data.sections.length > 0 && (
        <div className="flex justify-end space-x-2">
          {expandControls === "split" ? (
            <>
              <button type="button" onClick={expandAll} className={`text-base font-medium ${linkClass}`}>
                Expand All
              </button>
              <span className="text-gray-300">|</span>
              <button type="button" onClick={collapseAll} className={`text-base font-medium ${linkClass}`}>
                Collapse All
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={allOpen ? collapseAll : expandAll}
              className={`text-xs font-medium ${linkClass}`}
            >
              {allOpen ? "Collapse All" : "Expand All"}
            </button>
          )}
        </div>
      )}

      {data.sections.map((section, i) => {
        const hasTitle = Boolean(section.title?.trim());
        const open = layout === "open" || !hasTitle || expanded.has(i);
        const body =
          section.blocks && section.blocks.length > 0
            ? section.blocks.map((block, j) => (
                <Block key={j} text={block.text} variant={block.variant} renderMarkdown={renderMarkdown} />
              ))
            : section.content && <Block text={section.content} renderMarkdown={renderMarkdown} />;

        return (
          <section key={i} className="rounded-lg border border-gray-200">
            {hasTitle &&
              (layout === "open" ? (
                <div className="border-b border-gray-100 p-4">
                  <h3 className="text-base font-bold text-gray-900">{section.title}</h3>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={expanded.has(i)}
                  className="flex w-full items-center justify-between gap-2 p-4 text-left transition-colors hover:bg-gray-50"
                >
                  <h3 className="text-left text-base font-bold text-gray-900">{section.title}</h3>
                  <ChevronIcon open={expanded.has(i)} />
                </button>
              ))}
            {open && (
              <div
                className={`space-y-3 px-4 text-left ${
                  layout === "open" || !hasTitle ? "py-4" : "pb-4"
                }`}
              >
                {body}
              </div>
            )}
          </section>
        );
      })}

      {footer === "none" ? (
        whatsNewLines.length > 0 && (
          <WhatsNew lines={whatsNewLines} version={version} tone={whatsNewTone} padding="p-3" />
        )
      ) : (
        <div className={`${footerGap ? "mt-6 " : ""}space-y-4 rounded-lg border p-5 ${footerBoxClass}`}>
          <p className="text-center text-xs text-gray-600">
            <strong>Version:</strong> {version}
            {data.effectiveDate?.trim() && (
              <>
                <br />
                <strong>Effective Date:</strong> {data.effectiveDate}
              </>
            )}
            {data.lastUpdated?.trim() && (
              <>
                <br />
                <strong>Last Updated:</strong> {data.lastUpdated}
              </>
            )}
            {data.entity?.trim() && (
              <>
                <br />
                <strong>Entity:</strong> {data.entity}
              </>
            )}
          </p>
          {footerNote && <p className="text-center text-xs text-gray-600">{footerNote}</p>}
          {whatsNewLines.length > 0 && (
            <WhatsNew lines={whatsNewLines} version={version} tone={whatsNewTone} padding="p-4" />
          )}
        </div>
      )}
    </div>
  );
}

export type ConsentContentProps = {
  /** Stored consent description: structured policy JSON or legacy Markdown. */
  readonly content: string;
  readonly policyName: string;
  readonly version: number;
  readonly whatsNew?: string | null;
  readonly renderMarkdown: RenderMarkdown;
  /** Render the user-facing document header (for screens without their own). */
  readonly header?: { readonly title: string; readonly onClose?: () => void };
};

/** Renders any public consent/policy wording (not the DPA) from its stored description. */
export function ConsentContent({
  content,
  policyName,
  version,
  whatsNew,
  renderMarkdown,
  header,
}: ConsentContentProps) {
  const data = parseStructuredPolicy(content);
  const whatsNewLines = (whatsNew ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const body = data ? (
    <PolicyDocument
      data={data}
      policyName={policyName}
      version={version}
      whatsNew={whatsNew}
      renderMarkdown={renderMarkdown}
    />
  ) : (
    <div className="space-y-4">
      {renderMarkdown(content)}
      {whatsNewLines.length > 0 && (
        <WhatsNew lines={whatsNewLines} version={version} tone="blue" padding="p-3" />
      )}
    </div>
  );
  if (!header) return body;

  return (
    <>
      <PolicyDocumentHeader
        title={header.title}
        policyName={policyName}
        version={version}
        effectiveDate={data?.effectiveDate}
        onClose={header.onClose}
      />
      <div className="p-6">{body}</div>
    </>
  );
}
